import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import { IContractDetailsResponseData } from "../../../../setup/api/services/contract/models";
import { AddOnFeeCategory } from "../../../../setup/api/services/contract/models";

import styles from "./buyerFeeBreakdown.module.scss";

interface Props {
  contract: IContractDetailsResponseData;
}

const BuyerFeeBreakdown = ({ contract }: Props) => {
  const { t } = useTranslation();

  const currentUserAmount = useMemo(() => {
    return contract?.itemPrice;
  }, [contract]);

  const shippingFee = useMemo(() => {
    return (
      contract?.addonFeesList?.find(
        (fee) => fee.type === AddOnFeeCategory.SHIPPING_FEES
      )?.amount || 0
    );
  }, [contract?.addonFeesList]);

  const feeCommissionRate = useMemo(() => {
    return (
      +(
        contract?.addonFeesList?.find(
          (fee) => fee.type === AddOnFeeCategory.SERVICE_FEES
        )?.amount || 0
      ) / 100
    );
  }, [contract?.addonFeesList]);

  const totalAmountWithFee = useMemo(() => {
    const amount = currentUserAmount + shippingFee;
    return +(amount / (1 - feeCommissionRate)).toFixed(2);
  }, [currentUserAmount, shippingFee, feeCommissionRate]);

  const subTotalHaraj = useMemo(() => {
    return currentUserAmount + shippingFee;
  }, [currentUserAmount, shippingFee]);

  const serviceFeeValue = useMemo(() => {
    return totalAmountWithFee - subTotalHaraj;
  }, [totalAmountWithFee, subTotalHaraj]);

  const paidHarajTransaction = useMemo(() => {
    return (contract?.transactions as any[])?.find(
      (transaction) =>
        (transaction?.type === "CUSTOMER_PAY" ||
          transaction?.type === "MANUAL_BANK_TRANSFER") &&
        transaction?.status === "SUCCESS"
    );
  }, [contract?.transactions]);

  const paidPaymentInfo = useMemo(() => {
    const method = paidHarajTransaction?.paymentMethod;
    if (!method) return null;

    if (method === "CARD") {
      return {
        label: t("lbl_bank_card"),
        icon: "/assets/images/svg/visa.svg",
      };
    }

    switch (method) {
      case "APPLE_PAY":
        return {
          label: "Apple Pay",
          icon: "/assets/images/svg/apple-pay.svg",
        };
      case "STCPAY":
        return {
          label: t("lbl_pay_with_stc_pay"),
          icon: "/assets/images/svg/stc-pay.svg",
        };
      case "TABBY":
        return {
          label: t("lbl_pay_with_tabby"),
          icon: "/assets/images/svg/tabby.svg",
        };
      case "TAMARA":
        return {
          label: t("lbl_pay_with_tamara"),
          icon: "/assets/images/svg/Tamara.svg",
        };
      case "MANUAL_BANK_TRANSFER":
        return {
          label: t("lbl_bank_transfer"),
          icon: "/assets/images/svg/bank-icon.svg",
        };
      default:
        return {
          label: method,
          icon: "",
        };
    }
  }, [paidHarajTransaction, t]);

  return (
    <>
      <div className={styles.harajFeeBreakdownCard}>
        <div className={styles.harajFeeRow}>
          <span className={styles.harajFeeLabel}>{t("lbl_payment_title")}</span>
          <span className={styles.harajFeeValue}>
            {Number(currentUserAmount).toFixed(2)} {t("lbl_saudi_currency")}
          </span>
        </div>

        <div
          className={classNames(
            styles.harajFeeRow,
            styles.harajFeeBreakdownDivider
          )}>
          <span className={styles.harajFeeLabel}>{t("SHIPPING_FEE")}</span>
          <span className={styles.harajFeeValue}>
            {Number(shippingFee).toFixed(2)} {t("lbl_saudi_currency")}
          </span>
        </div>

        <div className={styles.harajFeeRow}>
          <span className={styles.harajFeeLabel}>
            {t("lbl_fee_breakdown_subtotal")}
          </span>
          <span className={styles.harajFeeValue}>
            {Number(subTotalHaraj).toFixed(2)} {t("lbl_saudi_currency")}
          </span>
        </div>

        {!!paidHarajTransaction && (
          <>
            <div
              className={classNames(
                styles.harajFeeRow,
                styles.harajFeeBreakdownDivider
              )}>
              <span className={styles.harajFeeLabelWithInfo}>
                {`${t("lbl_fee_breakdown_service_fee")}`}
              </span>
              <span className={styles.harajFeeValue}>
                {Number(serviceFeeValue).toFixed(2)} {t("lbl_saudi_currency")}
              </span>
            </div>

            <div className={styles.harajFeeRow}>
              <span className={styles.harajFeeLabel}>{t("lbl_total")}</span>
              <span className={styles.harajFeeValue}>
                {Number(totalAmountWithFee).toFixed(2)}{" "}
                {t("lbl_saudi_currency")}
              </span>
            </div>
          </>
        )}
      </div>

      {paidPaymentInfo && (
        <div className={styles.selectedMethodCard}>
          <div className={styles.selectedMethodContainer}>
            {paidPaymentInfo.icon && (
              <img
                src={paidPaymentInfo.icon}
                alt={paidPaymentInfo.label}
                className={styles.selectedMethodIcon}
              />
            )}
            <div className={styles.selectedMethodInfo}>
              <p className={styles.selectedMethodSubLabel}>
                {t("lbl_payment_method")}
              </p>
              <p className={styles.selectedMethodLabel}>
                {paidPaymentInfo.label}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BuyerFeeBreakdown;
